/**
 * sealManager — Sprint 2
 * Sealing a trip closes its book: it becomes archived + stamped with sealedAt,
 * its open threads (cabos sueltos) are captured, the index is flagged, and the
 * derived atlas is recomputed. All additive and offline-first.
 *
 * Decision (confirmed): sealing = status 'archived' + sealedAt. The Book View
 * reads sealedAt to go read-only. The index keeps a parallel `sealed` map so the
 * archive can be listed without loading whole trips.
 */
import dataManager from './DataManager';
import storageManager from './storageManager';
import loadTripPois from './loadTripPois';
import { poiSlug, haversineM } from './reveal';
import { recomputeAtlas } from './atlasManager';
import { recomputePostcards } from './postcardManager';

const cityKey = (city) => poiSlug(city || '');

// ~30 km around the city — keeps gem threads to the destination, not the country.
const SEAL_CITY_RADIUS_M = 30000;

const median = (arr) => {
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// Anchor the city geographically (language-agnostic): prefer the city's own POIs
// (non-quirky carry true city coords), else the trip's planned timeline coords.
function cityCenter(pois, trip) {
  let pts = pois.filter(p => p.category !== 'quirky' && typeof p.lat === 'number' && typeof p.lng === 'number');
  if (pts.length === 0) {
    pts = [];
    for (const acts of Object.values(trip?.timeline || {})) {
      for (const a of (acts || [])) {
        if (typeof a.lat === 'number' && typeof a.lng === 'number') pts.push(a);
      }
    }
  }
  if (pts.length === 0) return null;
  return { lat: median(pts.map(p => p.lat)), lng: median(pts.map(p => p.lng)) };
}

/**
 * Suggest open threads from the trip's own data (no user input yet). Two
 * sources, both tolerated-empty:
 *   • planned POIs never marked visited (from the timeline)
 *   • hidden gems that never surfaced (Sprint 1 whisper spots)
 * @returns {Promise<OpenThread[]>}
 */
export async function captureOpenThreads(trip) {
  const out = [];
  const seen = new Set();
  const cid = cityKey(trip?.destination?.city);

  // 1) planned-but-not-visited
  for (const acts of Object.values(trip?.timeline || {})) {
    for (const a of (acts || [])) {
      if (!a?.name || a.visited) continue;
      const poiId = poiSlug(a.name);
      const id = `t_${poiId}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ id, cityId: cid, poiId, text: a.name, kind: 'unvisited' });
    }
  }

  // 2) hidden gems of THIS CITY that never whispered. loadTripPois returns the
  // whole country's gems (so the live whisper can fire anywhere), so here we keep
  // only those near the destination — anchored on the city's real POIs.
  try {
    const pois = await loadTripPois(trip);
    const surfaced = new Set(trip?.surfacedPois || []);
    const center = cityCenter(pois, trip);
    for (const p of pois) {
      if (!p.hidden || surfaced.has(p.id)) continue;
      if (center && typeof p.lat === 'number' && typeof p.lng === 'number'
          && haversineM(center.lat, center.lng, p.lat, p.lng) > SEAL_CITY_RADIUS_M) continue;
      const id = `t_${p.id}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ id, cityId: cid, poiId: p.id, text: p.name, kind: 'unwhispered' });
    }
  } catch { /* gems are optional */ }

  return out;
}

/**
 * Seal a trip. Archives it, stamps sealedAt, persists the chosen threads, flags
 * the index, clears the active pointer if needed, and recomputes the atlas.
 * @param {string} tripId
 * @param {OpenThread[]} threads  threads to keep (already curated by the UI)
 * @returns {Promise<{ sealedAt: string, wasActive: boolean }>}
 */
export async function sealTrip(tripId, threads = []) {
  const trip = await dataManager.getTrip(tripId);
  if (!trip) throw new Error('Trip not found');

  const sealedAt = new Date().toISOString();
  await dataManager.updateTrip(tripId, {
    sealedAt,
    openThreads: Array.isArray(threads) ? threads : (trip.openThreads || []),
    status: 'archived',
    completedAt: trip.completedAt || sealedAt,
  });

  // If the sealed trip was the active one, clear the active pointer (same as
  // MyTrips' complete flow) so the app doesn't keep an archived trip active.
  const index = storageManager.getTripsIndex();
  const wasActive = index.activeTrip === tripId;
  if (wasActive) {
    index.activeTrip = null;
    index.lastModified = new Date().toISOString();
    storageManager.saveTripsIndex(index);
  }
  storageManager.markSealed(tripId, true);

  try { await recomputeAtlas(); } catch { /* atlas is a reconstructible cache */ }
  try { await recomputePostcards(); } catch { /* postcards are reconstructible too */ }

  return { sealedAt, wasActive };
}

/**
 * Find unresolved open threads left by PAST sealed trips to a given city, so a
 * later trip to the same place can pick them up. Matches on the normalized city
 * key. Excludes the current trip.
 * @returns {Promise<Array<OpenThread & { sourceTripId: string }>>}
 */
export async function findUnresolvedThreadsForCity(city, excludeTripId = null) {
  const cid = cityKey(city);
  if (!cid) return [];
  const all = await storageManager.getAllTrips().catch(() => []);
  const out = [];
  for (const t of all) {
    if (!t.sealedAt) continue;
    if (excludeTripId && t.tripId === excludeTripId) continue;
    for (const th of (t.openThreads || [])) {
      if (th.resolvedTripId || th.cityId !== cid) continue;
      out.push({ ...th, sourceTripId: t.tripId });
    }
  }
  return out;
}

/** Mark a past thread as picked up by a later trip; recompute the atlas. */
export async function resolveThread(sourceTripId, threadId, resolvedTripId) {
  const trip = await dataManager.getTrip(sourceTripId);
  if (!trip) return false;
  const threads = (trip.openThreads || []).map(th =>
    th.id === threadId ? { ...th, resolvedTripId } : th);
  await dataManager.updateTrip(sourceTripId, { openThreads: threads });
  try { await recomputeAtlas(); } catch { /* cache */ }
  return true;
}

/** Re-open a sealed trip (back to 'completed', un-stamped). */
export async function unsealTrip(tripId) {
  await dataManager.updateTrip(tripId, { sealedAt: null, status: 'completed' });
  storageManager.markSealed(tripId, false);
  try { await recomputeAtlas(); } catch { /* cache */ }
  return true;
}
